import Daypart from '@/Enums/Daypart.enum';
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  /*
   * Reject magnitudes past the exact-integer range.
   *
   * Observed live on the Geo > Country breakdown: clicks came back as
   * ~1.8446744e19 (2^64) with a matching CTR of 10,370,356,844,359% — a
   * negative count wrapping in an unsigned 64-bit column upstream. Rendered
   * literally it printed twenty digits straight through the neighbouring
   * column and read as a broken table rather than as bad data.
   *
   * Nothing above 2^53 can be a genuine impression/click/conversion count, and
   * past that JS can't represent it exactly anyway, so treat it as missing and
   * let it render as the standard "—".
   */
  if (Math.abs(n) >= Number.MAX_SAFE_INTEGER) return null;
  return n;
};

export const num = (value: unknown): string => {
  const n = toNumber(value);
  return n === null ? '—' : Math.round(n).toLocaleString();
};

/** Conversion counts keep up to 2 decimals: Google credits fractions, which `num` would round to 0. */
export const conversionCount = (value: unknown): string => {
  const n = toNumber(value);
  return n === null ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

/** Compact magnitude for KPI tiles (e.g. 5.7M, 54.9K). */
export const compactNum = (value: unknown): string => {
  const n = toNumber(value);
  if (n === null) return '—';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  return `${sign}${formatCompactMagnitude(abs)}`;
};

export const currency = (value: unknown): string => {
  const n = toNumber(value);
  return n === null
    ? '—'
    : n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

/** Compact currency for KPI tiles (e.g. $54.9K). */
export const compactCurrency = (value: unknown): string => {
  const n = toNumber(value);
  if (n === null) return '—';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  return `${sign}$${formatCompactMagnitude(abs)}`;
};

/**
 * Precise currency to the cent — for rate metrics like CPM where whole-dollar
 * rounding hides real differences (e.g. $10.14, not $10). Not for large totals.
 */
export const currencyPrecise = (value: unknown): string => {
  const n = toNumber(value);
  return n === null
    ? '—'
    : n.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

/**
 * Margin = Client Spend − Spend (#4447), derived purely client-side — never
 * fetched. Follows the same "in" convention as Client Spend itself: the API
 * omits a key rather than sending null for a group it can't carry, so both
 * keys must be present to compute a real answer. Blank (null or '', matching
 * `cell()`'s own blank test) is treated the same as absent — otherwise an
 * empty-string spend would coerce to 0 and Margin would show a real-looking
 * number for a row whose own Spend/Client Spend cell reads "—" (CodeRabbit,
 * PR #4591). Row is returned unchanged and `margin` stays unset, so `cell()`
 * renders the existing blank rather than a misleading zero.
 */
export const withMargin = (rows: ReportRow[]): ReportRow[] =>
  rows.map(row => {
    if (!('client_spend' in row) || !('spend' in row)) return row;
    const blank = (v: unknown) => v === null || v === '';
    if (blank(row.client_spend) || blank(row.spend)) return row;
    return { ...row, margin: Number(row.client_spend) - Number(row.spend) };
  });

/**
 * Share of impressions (not rows) in `rows` that resolved a real Client
 * Spend/CPM figure (#4448).
 *
 * ribeye-api's `enrichWithClientSpend` blanks a row's client_spend/client_cpm
 * entirely unless EVERY line item behind it had a resolvable sold CPM (the
 * same `'client_spend' in row || 'client_cpm' in row` test ImpressionsByType's
 * `clientMoneyUnavailable` already uses) — but it also now reports, on every
 * row, `client_spend_priced_impressions`: how many of THAT row's impressions
 * came from a resolved line item, even when the row as a whole stayed blank.
 *
 * Counting rows instead of impressions was the wrong weight (review, Lucas):
 * a 2K-impression row and a 20M-impression row counted the same, and a
 * Channel row that's 99% Beeswax-priced counted the same as one with no
 * pricing at all — any row with a non-Beeswax sliver read as fully
 * uncovered. Summing impressions instead answers "what share of delivery is
 * real", which is what #4448 actually asks.
 */
export interface ClientMoneyCoverage {
  pricedImpressions: number;
  totalImpressions: number;
}

/**
 * True when NOT ONE row in view carries client_spend/client_cpm — i.e. the
 * whole view is non-Beeswax delivery, or ribeye-api dropped the metric
 * entirely (truncated shadow query, `enrichWithClientSpend`). Moved here
 * (not kept local to ImpressionsByType, review flank#4655) so `reportExport.ts`
 * can reuse it too, without importing a component and creating a cycle back
 * through `useExportWidgetCsv.ts`.
 *
 * Doubles as the truncation gate: a truncated request returns every row
 * completely unchanged (CLIENT_SPEND_METRICS never got attached to any of
 * them), so this is already true whenever that happened — no separate
 * `truncated_fields` plumbing needed to suppress the same false "0% priced"
 * note a non-Beeswax view would otherwise show forever.
 *
 * Metric availability only — whether the Client Spend/CPM/Margin *columns*
 * have anything to show. Deliberately NOT the priced-impressions check below:
 * an older ribeye-api without #2481 still returns real client_spend figures,
 * and hiding those columns because the newer coverage field is missing would
 * hide valid money, not just an uncomputable note (review, CodeRabbit).
 */
export const clientMoneyUnavailable = (rows: ReportRow[]): boolean =>
  !rows.some(row => 'client_spend' in row || 'client_cpm' in row);

/**
 * True when no row carries `client_spend_priced_impressions` — a ribeye-api
 * deployed without #2481 (review, Lucas: unguarded deploy-order dependency).
 * Separate from `clientMoneyUnavailable` above: this only gates the coverage
 * *note*, which needs the priced-impressions denominator to mean anything.
 * Without it, the note would confidently claim "0% ... have a sold CPM"
 * right next to real Client Spend figures the columns are correctly showing.
 */
const coverageUnavailable = (rows: ReportRow[]): boolean => !rows.some(row => 'client_spend_priced_impressions' in row);

export const clientMoneyCoverage = (rows: ReportRow[]): ClientMoneyCoverage => {
  let pricedImpressions = 0;
  let totalImpressions = 0;
  for (const row of rows) {
    totalImpressions += toNumber(row.impressions) ?? 0;
    pricedImpressions += toNumber(row.client_spend_priced_impressions) ?? 0;
  }
  return { pricedImpressions, totalImpressions };
};

/**
 * Coverage as a whole-percent integer, clamped to [1, 99] whenever coverage is
 * genuinely partial — a 199/200 split would otherwise round to "(100%)", which
 * reads as fully covered in a note that only exists because it isn't, and a
 * 1/300 split would round to "(0%)" despite one priced row being real
 * (review, Lucas). Exact 0% and exact 100% (not produced by this function —
 * see the callers' own gates) are left alone.
 */
const coveragePercent = (pricedImpressions: number, totalImpressions: number): number => {
  if (pricedImpressions <= 0) return 0;
  const raw = Math.round((pricedImpressions / totalImpressions) * 100);
  return Math.min(99, Math.max(1, raw));
};

/**
 * Visible (never tooltip-only) disclosure for a Client Spend aggregate built
 * from a mix of priced and unpriced impressions — Client CPM and Margin
 * derive from Client Spend, so they share the same gap and the same note
 * (#4447).
 *
 * Null when every impression in view is priced (nothing to disclose) or
 * there's no delivery at all — a fully-covered or empty view has nothing to
 * say.
 */
export const clientMoneyCoverageNote = (rows: ReportRow[]): string | null => {
  if (coverageUnavailable(rows)) return null;
  const { pricedImpressions, totalImpressions } = clientMoneyCoverage(rows);
  if (totalImpressions === 0 || pricedImpressions >= totalImpressions) return null;
  const pct = coveragePercent(pricedImpressions, totalImpressions);
  return `${pct}% of impressions in view have a sold CPM. Client Spend, Client CPM and Margin are shown only for rows where every impression is priced; other rows show "—".`;
};

/**
 * Same disclosure, worded for a file rather than the screen (review, Lucas):
 * "in view" reads oddly outside the app, and a file's missing values are
 * empty cells, not "—".
 */
export const clientMoneyCoverageExportNote = (rows: ReportRow[]): string | null => {
  if (coverageUnavailable(rows)) return null;
  const { pricedImpressions, totalImpressions } = clientMoneyCoverage(rows);
  if (totalImpressions === 0 || pricedImpressions >= totalImpressions) return null;
  const pct = coveragePercent(pricedImpressions, totalImpressions);
  return `${pct}% of impressions have a sold CPM. Client Spend, Client CPM and Margin are shown only for rows where every impression is priced; other rows are blank.`;
};

/** Pick K/M/B after rounding so 999,951 → 1M (not 1000K). */
const formatCompactMagnitude = (abs: number): string => {
  const strip = (n: number) => n.toString().replace(/\.0$/, '');
  const asK = Number((abs / 1_000).toFixed(1));
  const asM = Number((abs / 1_000_000).toFixed(1));
  const asB = Number((abs / 1_000_000_000).toFixed(1));
  if (abs >= 1_000_000_000 || asM >= 1_000) return `${strip(asB)}B`;
  if (abs >= 1_000_000 || asK >= 1_000) return `${strip(asM)}M`;
  if (abs >= 1_000) return `${strip(asK)}K`;
  return Math.round(abs).toLocaleString();
};

export const percent = (value: unknown): string => {
  const n = toNumber(value);
  return n === null ? '—' : `${(n * 100).toFixed(2)}%`;
};

/**
 * Whole-percent, no decimals — VCR / video completion rate only (prep #0000,
 * product meeting decision: "can it just be 85%?"). Every other rate metric
 * (CTR, etc.) keeps `percent`'s two decimal places; this is deliberately not
 * a general-purpose formatter.
 */
export const percentWhole = (value: unknown): string => {
  const n = toNumber(value);
  return n === null ? '—' : `${Math.round(n * 100)}%`;
};

/** Relative share of a whole (e.g. 32%). */
export const sharePercent = (value: unknown, total: number): string => {
  const n = toNumber(value);
  if (n === null || total <= 0) return '—';
  const pct = (n / total) * 100;
  return `${pct >= 10 ? Math.round(pct) : pct.toFixed(1)}%`;
};

export const decimal = (value: unknown): string => {
  const n = toNumber(value);
  return n === null ? '—' : n.toFixed(2);
};

/** Hour-of-day 0-23 → "12 AM" … "11 PM" (for the daypart card). */
export const hourLabel = (value: unknown): string => {
  const n = toNumber(value);
  if (n === null || n < 0 || n > 23) return 'Unmapped';
  const period = n < 12 ? 'AM' : 'PM';
  const hour12 = n % 12 === 0 ? 12 : n % 12;
  return `${hour12} ${period}`;
};

/**
 * Named daypart buckets, in broadcast-day order.
 *
 * These are the platform's OWN dayparts (`Daypart`), not the Flank 2.0 / Lovable
 * reference's — reporting has to name a daypart the same way targeting does, or
 * an advertiser who bought Early Fringe can't find it in their report (review,
 * ribeye-api#1832). Derived from the enum so adding a daypart there carries here.
 */
export const DAYPART_BUCKETS = Object.values(Daypart);

export type DaypartBucket = Daypart;

/**
 * Hour-of-day → daypart.
 *
 * Declared as hour lists (not range comparisons) to mirror the `presetHours` of
 * DAYPART_PRESETS in CampaignManagement/CreateCampaign/LineItemForm/Daypart/
 * constants.ts, which is where these boundaries are defined for targeting — and
 * because Late Fringe wraps midnight, which reads as an off-by-one in `if` form.
 * The `daypart` dimension in ribeye-api's Services/FlankReporting/
 * dimensionRegistry.ts buckets server-side the same way; if one moves, move all three.
 */
const DAYPART_HOURS: ReadonlyArray<readonly [Daypart, readonly number[]]> = [
  [Daypart.LF, [23, 0, 1]],
  [Daypart.ON, [2, 3, 4, 5]],
  [Daypart.EM, [6, 7, 8]],
  [Daypart.DT, [9, 10, 11, 12, 13, 14, 15]],
  [Daypart.EF, [16, 17, 18]],
  [Daypart.PT, [19, 20, 21, 22]],
];

const DAYPART_BY_HOUR = new Map<number, Daypart>(
  DAYPART_HOURS.flatMap(([daypart, hours]) => hours.map(hour => [hour, daypart] as [number, Daypart])),
);

/**
 * Human-readable hour range for each daypart, derived from DAYPART_HOURS rather
 * than transcribed, so a boundary change can't leave the tooltip lying (FB-003).
 *
 * Bonneville asked for these in-product: "Late Fringe" and "Early Fringe" are
 * broadcast terms account teams were looking up in external documentation, and
 * two people reading the same chart were interpreting them differently.
 *
 * The start hour is the one whose preceding hour isn't in the bucket, which is
 * what makes Late Fringe (23, 0, 1) read as "11 PM - 2 AM" rather than
 * "12 AM - 11 PM".
 */
const hour12 = (hour: number): string => {
  const period = hour < 12 ? 'AM' : 'PM';
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h} ${period}`;
};

export const DAYPART_DEFINITIONS: ReadonlyArray<{ daypart: Daypart; range: string; hours: readonly number[] }> =
  DAYPART_HOURS.map(([daypart, hours]) => {
    const set = new Set(hours);
    const start = hours.find(hour => !set.has((hour + 23) % 24)) ?? hours[0];
    // End is exclusive — the hour the next daypart begins.
    const end = (start + hours.length) % 24;
    return { daypart, range: `${hour12(start)} - ${hour12(end)}`, hours };
  });

/** `Late Fringe` -> `11 PM - 2 AM`. Empty for anything unmapped. */
export const daypartRange = (daypart: string): string =>
  DAYPART_DEFINITIONS.find(d => d.daypart === daypart)?.range ?? '';

/**
 * `Late Fringe` -> `Late Fringe — 11 PM - 2 AM`, for surfaces with one line per
 * row rather than a two-line chart axis.
 *
 * The reason the ranges exist at all applies just as much to a table as to the
 * chart they were added for (#4278): "Late Fringe" and "Early Fringe" are
 * broadcast terms account teams were looking up externally, and two people
 * reading the same figure were interpreting them differently. A table is also
 * what gets exported and pasted into a deck, where the chart's axis labels
 * don't travel with it.
 *
 * Falls back to the bare name rather than appending a dangling separator, so an
 * unmapped or newly added daypart degrades to what it renders today.
 */
export const daypartLabel = (value: unknown): string => {
  const name = dimensionLabel(value);
  const range = daypartRange(name);
  return range ? `${name} — ${range}` : name;
};

/** Hour-of-day 0-23 → named daypart. */
export const daypartBucket = (value: unknown): DaypartBucket | 'Unmapped' => {
  const n = toNumber(value);
  if (n === null) return 'Unmapped';
  return DAYPART_BY_HOUR.get(Math.floor(n)) ?? 'Unmapped';
};

/**
 * Short forms deliberately: these are category-axis ticks in a half-width card,
 * where "Wednesday" x7 overflows and Chart.js silently drops every other label.
 */
const DAY_OF_WEEK_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** ISO weekday 1-7 (Mon-Sun) → day name (for the day-of-week card). */
export const dayOfWeekLabel = (value: unknown): string => {
  const n = toNumber(value);
  if (n === null || n < 1 || n > 7) return 'Unmapped';
  return DAY_OF_WEEK_NAMES[n - 1];
};

/**
 * API / warehouse placeholders that should render like a missing name.
 * Exported for the Publishers inventory rollup (flank#4643), which needs the
 * same "no usable name" test to sort an app bundle/domain into the Unknown /
 * Pending mapping bucket rather than treating it as a real unmapped name.
 */
export const BLANK_NAME_SENTINELS = new Set([
  'unknown',
  '(none)',
  'none',
  'n/a',
  'null',
  'undefined',
  // GAM's own domain sentinel (flank#4866) — distinct from the parenthesis-less
  // 'not applicable' PLACEHOLDER_DIMENSION_VALUES already had, which never
  // matched it.
  '(not applicable)',
  // DV360's own domain sentinel (flank#4866).
  'low volume inventory',
]);

/**
 * Beeswax stores raw internal names in the canonical fact tables using a
 * pipe-delimited hierarchy led by a parenthesized numeric id, e.g.
 * "(3268) Ribeye|MFRE|Fringe|MFRE/FBEV/6006|...". Only names shaped like that
 * get pipe-cut by `cleanName`; see its own doc comment for why.
 */
const BEESWAX_STYLE_NAME = /^\(\d+\)/;

/**
 * Return a human-readable display name for a campaign / item.
 *
 * Beeswax names are pipe-delimited ("(3268) Ribeye|MFRE|Fringe|..."), so for
 * those we keep only the text before the first "|" and the user sees
 * "(3268) Ribeye" rather than the full 150-char code string. The full name
 * is preserved for the `title` tooltip.
 *
 * Every other platform's names are not segmented that way, and cutting at the
 * first pipe destroys the only thing that tells rows apart: every Bing
 * campaign name starts with "Search |", collapsing them all to "Search"
 * (flank#4620); Simpli.fi order names hit the same issue (#4582, see
 * `orderLabel`). So non-Beeswax names keep everything up to the 60-char cap,
 * same as `orderLabel`.
 *
 * Null/empty and common sentinels ("Unknown", "(none)") → "—".
 */
export const cleanName = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  const s = String(value).trim();
  if (!s || BLANK_NAME_SENTINELS.has(s.toLowerCase())) return '—';
  if (BEESWAX_STYLE_NAME.test(s)) {
    const pipeIdx = s.indexOf('|');
    if (pipeIdx > 0) return s.slice(0, pipeIdx).trim() || '—';
  }
  return s.length > 60 ? `${s.slice(0, 60)}…` : s;
};

/**
 * Order names keep everything before the length cap, unlike `cleanName`, which
 * cuts at the first pipe (#4582).
 *
 * That rule works where the first segment identifies the thing. It does not
 * here: every Simpli.fi order name begins with the market code, so cutting at
 * the pipe rendered nineteen distinct Quantum Fiber orders as nineteen rows
 * reading "SEA", separable only by the id chip beside them.
 *
 * The pipes are the useful part — "SEA | Quantum Fiber MDU Q3 - $20 Offer | ER
 * | CO" is what tells one order from another.
 */
export const orderLabel = (name: unknown, id?: unknown): string => {
  if (name === null || name === undefined) return entityLabel(name, id, 'order');
  const s = String(name).trim();
  if (!s || BLANK_NAME_SENTINELS.has(s.toLowerCase())) return entityLabel(null, id, 'order');
  return s.length > 60 ? `${s.slice(0, 60)}…` : s;
};

/** Null / empty dimension labels → Unmapped (prefer over literal "(none)"). */
export const dimensionLabel = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Unmapped';
  const s = String(value);
  return s === '(none)' ? 'Unmapped' : s;
};

/**
 * Platform sentinels that mean "this delivery has no value here", written as a
 * value rather than a null.
 *
 * `dimensionLabel` only catches the null-shaped cases, so a platform that spells
 * its own gap out in words slips through as a real category. Measured on prod
 * 2026-09-08, a 30-day ZIP breakdown led with two such rows above every real ZIP:
 * `NULL` from GAM (135,212,646 impressions — GAM emits no ZIP at all) and the
 * literal string `Unknown` from DV360 (24,962,366). The largest actual ZIP was
 * 801,297, so the placeholders outranked it 169:1 and the breakdown read as
 * broken data rather than a coverage gap.
 *
 * Kept to values actually observed — a broad guess here would silently swallow a
 * real category named "Other".
 */
const PLACEHOLDER_DIMENSION_VALUES = new Set([
  'unknown',
  'unmapped',
  'not applicable',
  'n/a',
  // GAM's own domain sentinel (flank#4866) — with its parentheses, so it
  // doesn't match the bare 'not applicable' above.
  '(not applicable)',
  // DV360's own domain sentinel (flank#4866).
  'low volume inventory',
]);

/**
 * True when a dimension value is a placeholder rather than a place/thing.
 *
 * Callers should exclude these from ranked breakdowns AND disclose the share
 * they removed — hiding them silently restates the percentages against a smaller
 * base with nothing on screen saying so, which is its own reported bug.
 */
export const isPlaceholderDimension = (value: unknown): boolean =>
  dimensionLabel(value) === 'Unmapped' || PLACEHOLDER_DIMENSION_VALUES.has(String(value).trim().toLowerCase());

/**
 * Confirmed US spellings seen in `geo_country` (flank#4813) — each DSP writes
 * a different shape: Beeswax keeps the feed's alpha-3 country ('USA'),
 * DV360/AdsWizz write alpha-2 ('US'), Simpli.fi/Bing Ads write the full name
 * ('United States'). There's no single canonical shape to normalize to, so
 * this is an allowlist of known-US forms, not a lookup against one.
 */
const US_COUNTRY_VALUES = new Set(['usa', 'us', 'united states', 'united states of america']);

/**
 * True only for a GEOGRAPHY row whose country is a CONCRETE, positively
 * non-US value — never for a missing or placeholder one. GAM never populates
 * `geo_country` at all (NULL, the same gap as its missing geo_zip/geo_dma
 * elsewhere), and a DSP that can't resolve a country emits an explicit
 * "Unknown" rather than omitting the field — both read as "we don't know,"
 * not "this is foreign," so both pass through here rather than risk wiping
 * out a whole platform's map data over a country shape this can't recognize.
 * Only a value that's neither blank/placeholder nor a known US spelling is
 * excluded — this is the narrow, frontend-only mitigation flank#4813 scoped
 * to: a non-US postcode/city that happens to collide with a US one no longer
 * reaches boundary-matching. Positively disclosing non-US delivery elsewhere
 * is a separate, open product question the ticket left out of scope.
 */
export const isConfirmedNonUsCountry = (value: unknown): boolean => {
  if (isPlaceholderDimension(value)) return false;
  const raw = String(value ?? '')
    .trim()
    .toLowerCase();
  if (!raw) return false;
  return !US_COUNTRY_VALUES.has(raw);
};

/**
 * Display name for a row that may have no name at all.
 *
 * `cleanName` collapses null/empty/sentinel values to a bare "—", which reads as
 * a rendering glitch once it lands in a chart category or a table cell (the
 * observed "— GAM0" row, and a nameless bar in Top Campaigns). Two unnamed rows
 * also collapse into each other as identical "—" categories. Fall back to the
 * row's own id so each stays distinguishable and obviously deliberate.
 */
export const entityLabel = (name: unknown, id?: unknown, kind = 'item'): string => {
  const clean = cleanName(name);
  if (clean !== '—') return clean;
  const rawId = String(id ?? '').trim();
  const usableId = rawId && !BLANK_NAME_SENTINELS.has(rawId.toLowerCase()) ? rawId : '';
  return usableId ? `Unnamed ${kind} (${usableId})` : `Unnamed ${kind}`;
};

/**
 * The warehouse emits the seeded `DEVICES.NAME` vocabulary (TV, Mobile, Tablet,
 * Desktop, Games Console) — not raw Beeswax values (`CONNECTED_TV`,
 * `SET_TOP_BOX`, etc., flank#4882).
 *
 * Device "TV" and channel "CTV" are different axes, not the same thing — a
 * CTV-channel delivery can land on a non-TV device, and a TV device can
 * receive OLV or Display content. They're displayed the same today only as a
 * deliberate choice to match the adjacent channel card, not because the
 * taxonomy says they're interchangeable; reference taxonomies (AdCOM
 * DeviceType 3) and legacy Beeswax/DV360 reports call this device bucket
 * "Connected TV". The CTV/Connected TV/TV label question is still open on
 * flank#4882/#4908 and this mapping may need revisiting once it's decided.
 * Normalise for display only — the raw value still round-trips as the filter
 * value.
 */
export const DEVICE_DISPLAY: Record<string, string> = {
  TV: 'CTV',
  'Connected TV': 'CTV',
  'Games Console': 'Console',
};

export const deviceLabel = (value: unknown): string => {
  const base = dimensionLabel(value);
  return DEVICE_DISPLAY[base] ?? base;
};

/**
 * Applies the same CTV/Console normalisation to any OTHER device-labeling
 * resolver's output, without changing that resolver's own blank-value
 * handling. Before flank#4882, `deviceLabel` was the only device surface
 * that showed "CTV" — the Impressions-by-Type grid and CSV/XLSX export
 * showed the raw "TV" for the identical row, one display spot down.
 */
export const withDeviceDisplay =
  (labelFn: (value: unknown, id?: unknown) => string): ((value: unknown, id?: unknown) => string) =>
  (value, id) => {
    const label = labelFn(value, id);
    return DEVICE_DISPLAY[label] ?? label;
  };

/**
 * GAM's channel taxonomy bakes the platform name into the value itself
 * ("GAM Display"/"GAM Video") where every other DSP's same channel type
 * reads bare ("Display"). For a caller without canViewPlatform (flank#4741)
 * that's a second DSP leak alongside the Platform/DSP chip — cosmetic only,
 * the raw value still round-trips as the filter/series key.
 */
const PLATFORM_PREFIXED_CHANNEL_LABELS: Record<string, string> = {
  'GAM Display': 'Display',
  'GAM Video': 'Video',
};

/** Applies the GAM-prefix strip above to an already-resolved label string. */
export const genericChannelLabel = (label: string, canViewPlatform: boolean): string =>
  canViewPlatform ? label : (PLATFORM_PREFIXED_CHANNEL_LABELS[label] ?? label);

export const channelLabel = (value: unknown, canViewPlatform: boolean): string =>
  genericChannelLabel(dimensionLabel(value), canViewPlatform);

/**
 * Wraps a chip's `labelFn` with the Channel-tab GAM-prefix mask, only on the
 * Channel chip and only without canViewPlatform — every other chip's values
 * don't encode a platform name, so wrapping them would be a no-op at best.
 * Shared by every widget whose Channel chip resolves row names through a
 * labelFn callback (ImpressionsByType, Conversions — flank#4741 QA), so they
 * can't drift on which chip key or permission flag gates the mask.
 */
export const maskedChannelLabelFn = (
  activeSubTab: string,
  canViewPlatform: boolean,
  labelFn: (value: unknown, id?: unknown) => string,
): ((value: unknown, id?: unknown) => string) =>
  activeSubTab === 'channel' && !canViewPlatform
    ? (value: unknown, id?: unknown) => genericChannelLabel(labelFn(value, id), canViewPlatform)
    : labelFn;

/**
 * A channel filter/chip option as offered to the user, plus the raw channel
 * value(s) it stands for when selected — see `mergeChannelOptionsForRestrictedViewer`.
 * `values` is a singleton everywhere except a restricted-viewer merge.
 */
export interface ChannelOption {
  value: string;
  label: string;
  values: string[];
}

/**
 * Masking GAM's prefixed channels (genericChannelLabel above) creates a real
 * collision, not just a cosmetic one: an org with both a true "Display"
 * channel and a "GAM Display" channel would otherwise offer a restricted
 * viewer two indistinguishable "Display" options — selecting one silently
 * returns only ONE of the two channels' data, with no way to tell which, and
 * no way to select both (flank#4741 QA).
 *
 * So for a restricted viewer, options that mask to the same label are merged
 * into one entry whose `values` is the union of the raw values behind it —
 * selecting it filters by all of them together. The label never discloses
 * that it's a merge (no count, no tooltip listing the raw values); doing so
 * would leak the DSP identity the mask exists to hide. An unrestricted
 * viewer keeps every channel distinct, matching the raw data exactly — this
 * only ever collapses options, never the underlying rows a query returns.
 */
export const mergeChannelOptionsForRestrictedViewer = (
  options: ReadonlyArray<{ value: string; label: string }>,
  canViewPlatform: boolean,
): ChannelOption[] => {
  if (canViewPlatform) return options.map(o => ({ ...o, values: [o.value] }));
  const groups = new Map<string, ChannelOption>();
  for (const o of options) {
    const label = genericChannelLabel(o.label, false);
    const existing = groups.get(label);
    if (existing) existing.values.push(o.value);
    else groups.set(label, { value: label, label, values: [o.value] });
  }
  return [...groups.values()];
};

/**
 * Canonical `DSP_TYPE` → the platform name shown to users. The warehouse stores
 * lowercase slugs; every surface should render these instead, so the KPI cards,
 * the platform table and the filter pills agree.
 */
export const DSP_DISPLAY: Record<string, string> = {
  dv360: 'DV360',
  simplifi: 'Simpli.fi',
  // Renamed from "Beeswax" to describe what clients are actually buying,
  // not the ad-server vendor behind it (flank#4914). A per-whitelabel
  // override set in Directus (flank#4469) still wins over this default.
  beeswax: 'Buyer Cloud',
  adswizz: 'AdsWizz',
  gam: 'GAM',
  googleads: 'Google Ads',
  bingads: 'Bing Ads',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  metaads: 'Meta',
  asellus: 'Asellus',
  neuron: 'Neuron',
};

/**
 * DSPs present in DSP_DISPLAY for labelling but NOT yet queryable, so they must not
 * be offered as filters. Selecting one can only empty the report.
 *
 * LinkedIn has no adapter in the ingestion repo, no schema in Snowflake, and no rows
 * in canonical — its ingestion is #3702. It reached the filter bar because the pills
 * derive from DSP_DISPLAY, which is a *label* map; being able to spell a platform's
 * name is not the same as having its data. This is the same defect the channel list
 * already fixed (flank#3701), on the axis that fix didn't cover.
 *
 * Remove an entry here the moment its data lands — flank#4525 AC5.
 */
export const DSP_NOT_QUERYABLE: ReadonlySet<string> = new Set(['linkedin']);

/** The DSPs a user may filter by: everything DSP_DISPLAY can label, minus the unqueryable. */
export const SELECTABLE_DSPS: ReadonlyArray<{ value: string; label: string }> = Object.entries(DSP_DISPLAY)
  .filter(([value]) => !DSP_NOT_QUERYABLE.has(value))
  .map(([value, label]) => ({ value, label }));

export const dspLabel = (value: unknown): string => {
  const base = dimensionLabel(value);
  const key = base.toLowerCase();
  // Own-property check: a value like "constructor" would otherwise resolve off
  // Object.prototype and return a function instead of a label.
  return Object.hasOwn(DSP_DISPLAY, key) ? DSP_DISPLAY[key] : base;
};

/** Relative time from an ISO timestamp (e.g. "3 minutes ago"). */
export const timeAgo = (iso: string | undefined | null): string | null => {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return null;
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Freshness label for a whole-day value, e.g. "3 days ago".
 *
 * Not `timeAgo`: the API reports the last DATE a platform has delivery for, and
 * feeding a date to an hour-precision formatter breaks twice. `Date.parse`
 * treats a bare `YYYY-MM-DD` as UTC midnight but a date-*time* as local, so the
 * headline skews by the viewer's offset; and the elapsed hours keep climbing
 * through the day, so identical data read "73 hours" at 01:00 and "95 hours" by
 * 23:00.
 *
 * Comparing calendar days in the viewer's own timezone keeps the answer stable
 * until the data itself changes — which is the point of the number.
 */
/** Whole calendar days between a `YYYY-MM-DD` value and today, in the viewer's own timezone. */
export const daysStale = (value: string | undefined | null): number | null => {
  if (!value || !DATE_ONLY.test(value)) return null;

  const [y, m, d] = value.split('-').map(Number);
  const then = new Date(y, m - 1, d);
  // The shape check above passes "2026-02-30", which the Date constructor rolls
  // silently into March — a wrong label rather than no label. Round-trip it.
  if (then.getFullYear() !== y || then.getMonth() !== m - 1 || then.getDate() !== d) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today.getTime() - then.getTime()) / 86_400_000);
};

export const daysAgoLabel = (value: string | undefined | null): string | null => {
  if (!value) return null;
  if (!DATE_ONLY.test(value)) return timeAgo(value);

  const days = daysStale(value);
  if (days === null) return null;
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
};

/** Most recent timestamp in a dsp → ISO freshness map. */
export const latestFreshnessIso = (freshness: Record<string, string>): string | null => {
  let latest: string | null = null;
  let latestMs = -Infinity;
  for (const iso of Object.values(freshness)) {
    const ms = Date.parse(iso);
    if (Number.isFinite(ms) && ms > latestMs) {
      latestMs = ms;
      latest = iso;
    }
  }
  return latest;
};

/**
 * Oldest timestamp in the map — the point through which EVERY DSP has reported,
 * which is the only honest headline for a combined report.
 *
 * Taking the most recent instead flatters the data: measured 2026-07-30, beeswax
 * had ingested 2026-07-28 while gam / dv360 / simplifi / adswizz were stuck on
 * 2026-07-24, so "last updated 33 hours ago" was true of one DSP out of five and
 * hid a six-day gap in the other four.
 */
export const oldestFreshnessIso = (freshness: Record<string, string>): string | null => {
  let oldest: string | null = null;
  let oldestMs = Infinity;
  for (const iso of Object.values(freshness)) {
    const ms = Date.parse(iso);
    if (Number.isFinite(ms) && ms < oldestMs) {
      oldestMs = ms;
      oldest = iso;
    }
  }
  return oldest;
};

/** Per-DSP freshness, oldest first — the detail behind the headline. */
export const freshnessByDsp = (freshness: Record<string, string>): Array<{ dsp: string; iso: string }> =>
  Object.entries(freshness)
    .filter(([, iso]) => Number.isFinite(Date.parse(iso)))
    .map(([dsp, iso]) => ({ dsp, iso }))
    .sort((a, b) => Date.parse(a.iso) - Date.parse(b.iso));

/** Largest numeric value of `key` across rows — used to scale bar widths. */
export const maxOf = (rows: ReportRow[], key: string): number =>
  rows.reduce((max, row) => Math.max(max, toNumber(row[key]) ?? 0), 0);

export const sumOf = (rows: ReportRow[], key: string): number =>
  rows.reduce((sum, row) => sum + (toNumber(row[key]) ?? 0), 0);

export const ratio = (value: unknown, max: number): number => {
  const n = toNumber(value);
  if (n === null || max <= 0) return 0;
  return Math.max(0, Math.min(1, n / max));
};

/**
 * Canonical `conversions` is attribution-based and includes post-impression
 * (view-through) attribution — Beeswax's only conversion column is
 * `ip_conversions`, which is IP/household-graph attributed. So the metric
 * legitimately exceeds clicks: over a 30-day window Beeswax reported 749,653
 * conversions against 16,952 clicks, 44 per click, while click-driven DV360 sat
 * at 0.11. Labelling it "Convs." beside a "Clicks" column read as a bug and was
 * filed as one twice (#3971 item 4, #4390).
 *
 * There is no click-through conversion column in the feed to split against —
 * Beeswax doesn't deliver one — so the fix is to say what the number measures
 * rather than to change it.
 *
 * Every surface that renders the metric reads these, so the three of them can't
 * drift into disagreeing about what it's called.
 */
export const CONVERSIONS_LABEL = 'Attr. Convs.';
export const CONVERSIONS_LABEL_LONG = 'Attributed Conversions';
export const CONVERSIONS_NOTE =
  'Attributed conversions include post-impression (view-through) attribution, so this count can exceed clicks.';

/**
 * Whether the caller's Attribution permission stripped conversion data off a
 * report response (flank#4648's Attribution permission + ATTRIBUTION_WIDGET_FLAG
 * — the API silently drops these fields and reports it here rather than
 * erroring). Shared by Conversions and ConversionsBreakdown (flank#4741) so
 * both widgets hide on the same signal instead of each re-implementing the
 * check — pure and exported so it's testable without mounting either.
 */
export const isAttributionRestricted = (restrictedFields: string[]): boolean =>
  restrictedFields.includes('dimensions.conversion_type') || restrictedFields.includes('metrics.conversions');

/**
 * Frequency is impressions ÷ reach, and canonical's REACH aggregates ADDITIVELY
 * across days — a person reached on 20 days counts 20 times in the denominator.
 * So any multi-day "frequency" is really the average of that window's daily
 * frequencies, not the number of times one person saw an ad over the period.
 *
 * Measured over 30 days (#4367): beeswax reads 1.63 against a daily range of
 * 1.57-1.68, dv360 reads 1.96 against 1.87-2.10. Real deduplicated reach would
 * make the 30-day figure far HIGHER than any single day, since the same
 * impressions divide by a much smaller unique audience. It is flat instead,
 * which is what proves the denominator is summed.
 *
 * 1.6 over a month on CTV reads plausibly enough that nobody questioned it,
 * which is exactly why it needs saying on screen rather than in a doc. True
 * cumulative frequency needs deduplicated reach at a period grain, which no
 * source gives us today — tracked in ribeyemedia/ingestion#232.
 */
export const FREQUENCY_LABEL = 'Avg. Daily Freq.';
export const FREQUENCY_LABEL_LONG = 'Avg. Daily Frequency';
export const FREQUENCY_NOTE =
  "Impressions ÷ reach, averaged across the days in view. Reach isn't deduplicated across days, so this is the average of daily frequencies — not how many times one person saw an ad over the whole period.";

/**
 * Frequency cannot be combined across platforms, so every surface that shows it
 * has to agree on when to withhold it.
 *
 * Measured over 30 days (#4367): blending all rows gives 338M impressions over
 * 104M reach = 3.24, but 266M of those impressions come from sources reporting
 * no reach at all (GAM alone is 132M at zero), so the numerator is inflated.
 * Restricting to rows that DO report reach gives 0.69 — below 1, which is
 * impossible — because DV360 reports reach on dedicated rows where impressions
 * are 0, so that filter discards the impressions belonging with the reach.
 *
 * Neither form is salvageable by arithmetic. Per-DSP is sound (dv360 1.96,
 * beeswax 1.63, simplifi 2.40, tiktok 1.04) because each DSP's reach rows and
 * impression rows are its own. So frequency is shown for exactly one platform
 * and withheld otherwise, rather than blended into a plausible wrong number.
 * Whether cross-DSP dedup is achievable at all is with Andrei on
 * ribeyemedia/ingestion#232.
 */
export const isFrequencyComparable = (dsps: string[] | undefined): boolean => dsps?.length === 1;

export const FREQUENCY_UNAVAILABLE_NOTE =
  "Frequency can't be combined across platforms — each reports reach for its own audience, and the same person seen on two platforms counts twice. Select a single platform to see it.";

/**
 * Same explanation as FREQUENCY_UNAVAILABLE_NOTE, minus the "select a single
 * platform" instruction — a restricted viewer (no canViewPlatform) can't act
 * on that advice and has no Platform control to follow it with (preprod QA,
 * review: a Client-role tooltip pointed them at a control they can't see).
 */
export const FREQUENCY_UNAVAILABLE_NOTE_RESTRICTED =
  "Frequency can't be combined across platforms — each reports reach for its own audience, and the same person seen on two platforms counts twice.";

/**
 * Both of these tell the reader how to get the number back, so the instruction has to name a
 * control that exists and a platform that works. The first wording did neither: "break down by
 * Platform" reads as the page-level grouping toggle literally labelled "By Platform", which does
 * NOT satisfy the gate (it reads this table's own dims, not the page grouping), and "filter to
 * one" is only true for the safe sets below — on DV360, Simpli.fi or GAM you follow the advice
 * exactly and the same tooltip repeats it at you. Both were reported from preprod QA (#4572).
 */
// Interpolated rather than hardcoded (flank#4914, Lucas review): these used
// to name "Beeswax"/"AdsWizz" literally, which read as a broken instruction
// the moment Beeswax's default label changed to "Buyer Cloud" — a reader
// filtering to a platform called "Beeswax" that no longer appears anywhere
// in the UI.
export const CONVERSIONS_UNAVAILABLE_NOTE = `Conversions can't be combined across platforms — there is no universal pixel, and the platforms don't agree on what counts. Switch this table to the Platform breakdown, or filter to ${dspLabel('beeswax')} or ${dspLabel('adswizz')} — the other platforms don't report conversions reliably enough per row.`;

export const REACH_UNAVAILABLE_NOTE = `Reach can't be added up across platforms — each reports its own audience, so anyone reached on two platforms is counted twice. Switch this table to the Platform breakdown, or filter to ${dspLabel('adswizz')}, Simpli.fi or Meta — the other platforms don't report reach reliably enough per row.`;

/**
 * Canonical Beeswax reach is per hour row, so any sum of it overstates reach 7–14× (flank#4479).
 * Withheld until a view gets live reach; Beeswax-only Campaign and Device rows already do.
 */
export const BEESWAX_REACH_PENDING_NOTE = `${dspLabel('beeswax')} reach isn't deduplicated for this view yet — adding up its hourly rows would overstate it many times. For live reach, filter to ${dspLabel('beeswax')} only, with no Campaign, Line Item, Product, Channel, Device or Publisher filter, and use the Campaign or Device breakdown.`;

/**
 * Same explanation as BEESWAX_REACH_PENDING_NOTE, minus the platform name and
 * the "filter to X" instruction — a restricted viewer (no canViewPlatform)
 * can't see the Platform control or the platform's own label, same rationale
 * as REACH_UNAVAILABLE_NOTE_RESTRICTED / FREQUENCY_UNAVAILABLE_NOTE_RESTRICTED
 * below (preprod QA: this tooltip leaked DSP identity to a Client-role user).
 */
export const BEESWAX_REACH_PENDING_NOTE_RESTRICTED =
  "Reach isn't deduplicated for this view yet — adding up its hourly rows would overstate it many times.";

/**
 * Same explanation as REACH_UNAVAILABLE_NOTE, minus the DSP names and the
 * "switch to the Platform breakdown" instruction — both name a control and
 * platforms a restricted viewer (no canViewPlatform) can't see or use
 * (preprod QA, review: this tooltip leaked DSP identity to a Client-role
 * user via the Reach column's accessible-name text).
 */
export const REACH_UNAVAILABLE_NOTE_RESTRICTED =
  "Reach can't be added up across platforms — each reports its own audience, so anyone reached on two platforms is counted twice.";

/**
 * flank#4752 — CTR's denominator only counts impressions on media with a
 * click surface (e.g. Display, Video); channels that can't be clicked at all
 * (CTV, Audio, out-of-home) are excluded from both sides of the ratio rather
 * than diluting it. Static, policy-level wording — which channels count is
 * an org-level backend setting (`Whitelabel.clickableChannels`) with no
 * per-org plumbing into this UI today, so this names the policy, not a
 * specific channel list that could drift from what's actually configured.
 */
export const CTR_CLICKABLE_NOTE =
  "CTR only counts media with a click surface, like Display and Video. Channels that can't be clicked at all — CTV, Audio, out-of-home — aren't counted in either side of the ratio.";

/**
 * DSPs where reach reliably arrives on the SAME row as impressions. Verified
 * against canonical (30 days, 2026-08-20+): beeswax/adswizz/simplifi/metaads
 * co-occur ~100% of the time; gam/dv360/tiktok co-occur 0% — their reach
 * arrives on separate, impression-less rows (the shape DV360 already had per
 * #4402; GAM has since joined it, per live verification — review, flank#4479).
 *
 * This matters ONLY for a breakdown narrower than the whole DSP.
 * `isFrequencyComparable` above stays true for a lone dv360/gam filter with NO
 * further breakdown — SUM(reach) and SUM(impressions) both total correctly at
 * that grain even from separate rows, which is why #4402 called dv360's 1.96
 * sound. But filter to gam/dv360/tiktok and break down by anything else
 * (Device, Channel, …), and every impression-less reach row collapses into one
 * "unknown" bucket while impressions spread across the real ones — the same
 * "0.69, below 1, impossible" shape #4367 fixed, reproduced inside a single
 * platform (review, flank#4479). Not in this set means "not proven safe", not
 * "known bad" — keeps a DSP out until its rows are checked.
 */
const REACH_SAFE_FOR_BREAKDOWN_DSPS = new Set(['beeswax', 'adswizz', 'simplifi', 'metaads']);

/**
 * Same failure mode as reach, a different (narrower) safe set: verified
 * conversions/impressions co-occurrence is ~100% for beeswax/adswizz, but only
 * 72%/63% for dv360/simplifi — so a single-DSP conversions breakdown by Device
 * or Channel can hide a real minority of a dv360 or simplifi campaign's
 * conversions in an unknown bucket the same way reach does. gam/tiktok/metaads
 * carry no conversions today, so they're excluded rather than assumed safe.
 */
const CONVERSIONS_SAFE_FOR_BREAKDOWN_DSPS = new Set(['beeswax', 'adswizz']);

/**
 * Whether a row in a breakdown table is scoped to a single platform, and so can
 * carry reach and frequency.
 *
 * Two ways to get there: the breakdown is BY platform, in which case every row
 * is one DSP by construction; or the whole table is filtered to one platform
 * whose reach rows are known to co-occur with impressions
 * (REACH_SAFE_FOR_BREAKDOWN_DSPS), in which case every row is that DSP
 * whatever it's broken down by. Anything else — Channel, Device, Geo, or a
 * single DSP whose reach arrives on separate rows — puts more than one platform
 * (or an impression-less reach row) behind a single breakdown row. DSP-entity
 * breakdowns are single-platform per ROW rather than per table, so they're
 * decided row by row in reachRowNote below (#4604).
 *
 * Deliberately NOT just isFrequencyComparable(dsps): that check is sound for a
 * single-platform filter with no further breakdown, but not once one is added
 * (review, flank#4479) — this adds both the breakdown-dimension half and the
 * per-DSP row-shape half that a filter-length check alone can't see.
 */
export const isPerPlatformScope = (dsps: string[] | undefined, dims: string[]): boolean =>
  dims.includes('dsp') || (dsps?.length === 1 && REACH_SAFE_FOR_BREAKDOWN_DSPS.has(dsps[0]));

/** Same as isPerPlatformScope, gated to conversions' own (narrower) safe DSP set. */
export const isConversionsPerPlatformScope = (dsps: string[] | undefined, dims: string[]): boolean =>
  dims.includes('dsp') || (dsps?.length === 1 && CONVERSIONS_SAFE_FOR_BREAKDOWN_DSPS.has(dsps[0]));

/**
 * Breakdown dimensions whose value names one platform's own entity by
 * construction (`DSP_CAMPAIGN_ID`, `DSP_LINE_ITEM_ID`, `DSP_CREATIVE_ID` in
 * ribeye-api's dimensionRegistry) even when the table spans several (#4604).
 *
 * `advertiser` no longer fits that description with no platform filter: since
 * ribeye-api#2515 it groups by the canonical Ribeye advertiser id, which CAN
 * repeat across DSPs on purpose (Martin: one row per Ribeye advertiser by
 * default) — see isAdvertiserRollupNotAdditive and withEntityDsp below for how
 * reach/frequency and conversions stay correct on that specific row shape. It
 * stays in DSP_ENTITY_DIMS because narrowed to an explicit platform filter (or
 * on an unlinked advertiser) it's still one DSP-native id per row, same as the
 * other three.
 *
 * `order_group` is deliberately absent: it's a BLID billing id parsed out of
 * line-item names (ingestion order_group.py), bare digits with no DSP prefix,
 * and its UNMAPPED bucket is shared across DSPs by design — an Order row can
 * span platforms.
 */
const DSP_ENTITY_DIMS = ['advertiser', 'campaign', 'line_item', 'creative'];

export const isDspEntityScope = (dims: string[]): boolean => dims.some(d => DSP_ENTITY_DIMS.includes(d));

/**
 * The dimensions to query for a breakdown: an entity breakdown also asks for
 * `dsp`, so each row names its platform outright. Ids alone can't — Meta, TikTok,
 * Google Ads and Bing arrive as bare numerics (see dspFromEntityId). Entity ids
 * don't repeat across DSPs, so this adds a column without splitting rows.
 *
 * `advertiser` is the one exception, and only with no platform filter: since
 * ribeye-api#2515 its id is the canonical Ribeye advertiser id, which CAN repeat
 * across DSPs on purpose (Martin: Advertiser rolls up to one row per Ribeye
 * advertiser by default) — adding `dsp` there would re-fragment the exact rows
 * #2515 merged. Once `dsps` narrows to specific platforms the old per-row
 * disambiguation is still wanted (an advertiser can still appear once per
 * filtered platform), so `dsp` goes back in.
 */
export const withEntityDsp = (dims: string[], dsps?: string[]): string[] => {
  if (!isDspEntityScope(dims) || dims.includes('dsp')) return dims;
  if (dims.includes('advertiser') && !dsps?.length) return dims;
  return [...dims, 'dsp'];
};

/**
 * Whether Impressions by Type shows its conversions column. Unlike reach, an
 * entity breakdown needs no per-row rule: every row is one platform's entity,
 * and conversions without impressions (post-flight lookback) still belong to it.
 * Verified against canonical (30 days to 2026-09-28): 92–100% of each DSP's
 * conversions land in an entity that also has impressions.
 *
 * The no-filter `advertiser` rollup is the one exception to "every row is one
 * platform's entity" (see withEntityDsp) — this still shows the column (a
 * rolled-up row's conversions aren't wrong when it's genuinely one platform),
 * but a row isAdvertiserRollupNotAdditive flags gets "Not additive" per-row
 * instead, the same way reachRowNote withholds reach per-row.
 */
export const isConversionsColumnShown = (dsps: string[] | undefined, dims: string[]): boolean =>
  isConversionsPerPlatformScope(dsps, dims) || isDspEntityScope(dims);

/**
 * Tercept's source prefixes (ingestion source_ids.py SOURCE_PREFIXES), which
 * canonical keeps on AdsWizz, DV360, GAM and Simpli.fi ids — DV360's advertiser
 * is `DV3…:DV3…`, still led by the prefix. Beeswax is rebuilt as
 * `<buzz_key>:<numeric id>` (e.g. `ribeye:7343`), with `BWX<id>` on older rows.
 * Meta, TikTok, Google Ads and Bing arrive as bare numerics, so a bare id names
 * no platform and resolves to null rather than a guess.
 */
const DSP_ID_PREFIXES: Array<[string, string]> = [
  ['AWZ', 'adswizz'],
  ['BWX', 'beeswax'],
  ['DV3', 'dv360'],
  ['GAM', 'gam'],
  ['SFI', 'simplifi'],
];

export const dspFromEntityId = (id: unknown): string | null => {
  const s = String(id ?? '').trim();
  const prefixed = DSP_ID_PREFIXES.find(([prefix]) => s.startsWith(prefix));
  if (prefixed) return prefixed[1];
  return /^[A-Za-z].*:\d+$/.test(s) ? 'beeswax' : null;
};

export const ENTITY_REACH_NOTE =
  "Each row is one platform's own advertiser, campaign, line item or creative, so nothing is combined across platforms. A row reads “—” where its reach can't be matched to it — hover the cell to see why.";

export const REACH_UNKNOWN_PLATFORM_NOTE =
  "This row's platform can't be read from its ID, and not every platform reports reach on the same rows as impressions, so it isn't shown. Filter to Meta to see Meta's, or switch this table to the Platform breakdown for each platform's total.";

const reachSeparateRowsNote = (dsp: string): string =>
  `${dspLabel(dsp)} reports reach on separate rows from its impressions, so it can't yet be matched to a single row reliably. Switch this table to the Platform breakdown to see ${dspLabel(dsp)}'s total.`;

const reachNotReportedNote = (dsp: string): string =>
  `${dspLabel(dsp)} didn't report reach for this row, so there's no reach or frequency to show — it isn't zero.`;

/**
 * Entity breakdowns where a DSP outside REACH_SAFE_FOR_BREAKDOWN_DSPS is still
 * safe: its reach arrives on separate, impression-less rows, but those rows carry
 * the same entity id as the impression rows, so SUM(reach) and SUM(impressions)
 * land on the same breakdown row. Verified against canonical (30 days to
 * 2026-09-28): for dv360 and tiktok at every entity level and gam at
 * advertiser, campaign and line item, 100% of reach sits in an entity that also
 * has impressions.
 *
 * GAM creative is out: every GAM reach row carries the placeholder creative id
 * `GAM`, so all of its reach would pile onto one fake creative.
 */
const REACH_SAFE_ENTITY_DIMS_BY_DSP: Record<string, string[]> = {
  dv360: ['advertiser', 'campaign', 'line_item', 'creative'],
  gam: ['advertiser', 'campaign', 'line_item'],
  tiktok: ['advertiser', 'campaign', 'line_item', 'creative'],
};

/**
 * Why a row's reach and frequency are withheld, or null when they're shown.
 * The one per-row rule behind the grid's cells and its CSV export (#4604).
 *
 * An Advertiser rollup (no platform filter) spanning more than one platform is
 * checked first and returns REACH_NOT_ADDITIVE_NOTE rather than falling into
 * the id-guessing logic below: its bare canonical id carries no DSP prefix,
 * so dspFromEntityId can't name a platform, and "the same person reached on
 * two platforms double-counts" is the real reason, not "can't be identified"
 * (see isAdvertiserRollupNotAdditive).
 *
 * A single-platform rollup row has the opposite problem: it IS safe, but
 * `withEntityDsp` doesn't add `dsp` there either (same reason — it would
 * re-fragment the rollup), and the canonical id still carries no DSP prefix
 * for `dspFromEntityId` to read. `single_dsp` (ribeye-api#2516) names the
 * platform directly for exactly this case, so it's checked before falling
 * back to the id guess (review, Lucas: without it, flank#4796 regressed
 * Reach for every linked single-platform advertiser, not just the
 * multi-platform ones).
 *
 * On a DSP-entity breakdown a row's platform comes from its `dsp` field (the
 * entity query carries it — see withEntityDsp), falling back to the id prefix.
 * The row is trusted when that DSP is in REACH_SAFE_FOR_BREAKDOWN_DSPS, or its
 * reach rows are verified to carry that entity's id (REACH_SAFE_ENTITY_DIMS_BY_DSP).
 * Outside the first set, zero reach beside real impressions means the platform
 * reported none for that row (e.g. 40 GAM campaigns, or Google Ads, which has
 * no reach) — "—", not a 0 that reads as nobody reached.
 *
 * `dsp` beside an entity dim only names the row's platform; it doesn't make the
 * row safe the way a Platform breakdown does (GAM creative is one platform and
 * still unsafe), so it's set aside before the per-platform check.
 */
export const reachRowNote = (
  dsps: string[] | undefined,
  dims: string[],
  row: ReportRow,
  canViewPlatform = true,
): string | null => {
  if (isAdvertiserRollupNotAdditive(dims, row)) return REACH_NOT_ADDITIVE_NOTE;
  const idDim = DSP_ENTITY_DIMS.find(d => dims.includes(d));
  const rowDsp =
    (typeof row.dsp === 'string' && row.dsp) ||
    (typeof row.single_dsp === 'string' && row.single_dsp) ||
    (dsps?.length === 1 ? dsps[0] : null) ||
    (idDim ? dspFromEntityId(row[idDim]) : null);
  if (rowDsp === 'beeswax') return canViewPlatform ? BEESWAX_REACH_PENDING_NOTE : BEESWAX_REACH_PENDING_NOTE_RESTRICTED;
  if (isPerPlatformScope(dsps, idDim ? dims.filter(d => d !== 'dsp') : dims)) return null;
  if (!idDim) return REACH_UNAVAILABLE_NOTE;
  const dsp =
    typeof row.dsp === 'string' && row.dsp
      ? row.dsp
      : typeof row.single_dsp === 'string' && row.single_dsp
        ? row.single_dsp
        : dspFromEntityId(row[idDim]);
  if (dsp === null) return REACH_UNKNOWN_PLATFORM_NOTE;
  if (REACH_SAFE_FOR_BREAKDOWN_DSPS.has(dsp)) return null;
  if (!(Number(row.reach) > 0)) return reachNotReportedNote(dsp);
  return REACH_SAFE_ENTITY_DIMS_BY_DSP[dsp]?.includes(idDim) ? null : reachSeparateRowsNote(dsp);
};

/**
 * Rows as the grid shows them: reach/frequency nulled wherever reachRowNote
 * withholds them, so an export can't carry a figure the screen reads as "—".
 * Nulled, not deleted — the file keeps its columns — and only where present,
 * so a row that never asked for them doesn't grow them.
 */
export const withReachGate = (
  rows: ReportRow[],
  dsps: string[] | undefined,
  dims: string[],
  canViewPlatform = true,
): ReportRow[] =>
  rows.map(row => {
    if (reachRowNote(dsps, dims, row, canViewPlatform) === null) return row;
    const out: ReportRow = { ...row };
    for (const key of ['reach', 'frequency']) if (key in out) out[key] = null;
    return out;
  });

/**
 * Whether a DSP-entity row is an Advertiser rollup spanning more than one
 * platform. The no-filter Advertiser default now groups by the canonical
 * Ribeye id (ribeye-api#2515, Martin: one row per Ribeye advertiser), so a
 * linked advertiser delivering on several platforms is one row, not one per
 * platform — and `withEntityDsp` deliberately stops asking for `dsp` there,
 * so `platform_count` (ribeye-api#2516) is the only way to tell.
 *
 * A single-platform rollup row is handled separately: it's safe to show, and
 * `single_dsp` (ribeye-api#2516) lets reachRowNote identify its platform the
 * same way it would via `dsp`. This only flags the >1 case, where the right
 * answer isn't a real number at all, it's "Not additive" (reusing the Total
 * row's own convention below, not a new one).
 */
export const isAdvertiserRollupNotAdditive = (dims: string[], row: ReportRow): boolean =>
  dims.includes('advertiser') && typeof row.platform_count === 'number' && row.platform_count > 1;

/**
 * Impressions by Type's Total row (flank#4645). Reach, Frequency and
 * Conversions are left out on purpose — not an oversight, see below — so the
 * caller renders "Not additive" for those three rather than looking up a
 * value that isn't here.
 */
export const NOT_ADDITIVE_LABEL = 'Not additive';

export const REACH_NOT_ADDITIVE_NOTE =
  "Reach can't be summed — the same person can show up in more than one row (two campaigns, two line items) or more than one platform within a single rolled-up Advertiser row, so adding them double-counts. Frequency (impressions ÷ reach) inherits the same problem.";

/**
 * Conversions gets the same treatment as reach rather than being summed,
 * even though the ticket (flank#4645) only names Reach/Frequency: the
 * column is already withheld by `isConversionsPerPlatformScope` for exactly
 * this reason — the same conversion can be attributed by more than one
 * platform — and that hazard doesn't go away once you're adding rows
 * together instead of blending platforms within one.
 */
export const CONVERSIONS_NOT_ADDITIVE_NOTE =
  "Conversions can overlap the same way reach does — attribution isn't exclusive between platforms, so the same conversion can be counted on more than one row. Summing the rows above would double-count it.";

/**
 * Computes the Total row's additive and rate values from `totalsRow` — the
 * ungrouped 'totals' widget's single row, the exact same query the KPI cards
 * (KpiStrip) read. Reusing that query, rather than summing the grid's own
 * (paged) rows, is what makes the Total match the KPI cards by construction:
 * both are the backend's SUM/ratio over every row matching the current
 * filters, not just the page in view.
 *
 * Additive metrics (impressions, spend, clicks, client_spend) are the
 * backend's own SUM. Rate metrics are recalculated FROM those totals, never
 * summed or averaged: ctr/vcr/client_cpm/cpc come straight from the backend,
 * which computes each as one ratio over the aggregate; cpm is derived
 * client-side the same way KpiStrip's `derive` does (spend ÷ impressions —
 * not requested as a backend metric by the `totals` widget). Margin follows
 * withMargin's own convention (client_spend − spend, only when both are
 * genuinely present).
 *
 * The 'in' checks mirror clientMoneyUnavailable/withMargin: the API omits a
 * key entirely for a metric this user can't see or a group it can't carry,
 * so a missing key must stay missing rather than coerce to a misleading 0.
 */
export const buildTotalRowValues = (totalsRow: ReportRow | undefined): ReportRow => {
  if (!totalsRow) return {};
  const out: ReportRow = {};
  for (const key of [
    'impressions',
    'spend',
    'clicks',
    'client_spend',
    'ctr',
    'vcr',
    'client_cpm',
    'cpc',
    // Social engagement (flank#4634) — additive, unlike Reach/Frequency.
    'link_clicks',
    'post_engagement',
    'video_plays',
    'video_completes',
    'reactions',
    'comments',
    'shares',
  ] as const) {
    if (key in totalsRow) out[key] = totalsRow[key];
  }
  const impressions = Number(totalsRow.impressions ?? 0);
  const spend = Number(totalsRow.spend ?? 0);
  const blank = (v: unknown) => v === null || v === undefined || v === '';
  // Same blank check as Margin below (review, CodeRabbit): a null spend
  // coerced to 0 by `Number(totalsRow.spend ?? 0)` would otherwise derive a
  // confident $0 CPM while the Spend cell right next to it shows "—".
  if ('spend' in totalsRow && !blank(totalsRow.spend) && impressions > 0) out.cpm = (spend / impressions) * 1000;
  if (
    'client_spend' in totalsRow &&
    'spend' in totalsRow &&
    !blank(totalsRow.client_spend) &&
    !blank(totalsRow.spend)
  ) {
    out.margin = Number(totalsRow.client_spend) - spend;
  }
  return out;
};

/**
 * Dimensions whose source table (`pickSourceTable`, ribeye-api) is APP/DOMAIN,
 * not PERFORMANCE — unlike every other Impressions-by-Type chip. The Total
 * row reads the ungrouped 'totals' widget, which carries no dims and so
 * always resolves to PERFORMANCE; on these chips its aggregate covers more
 * delivery than the rows below it, which only carry impressions matched to
 * an app or domain (flank#4649 review: Total ~308M vs rows ~176M/~200M for
 * Sep). flank#4645's AC requires the Total to match the KPI cards on every
 * tab, so it's still shown here — this only flags that a tooltip explaining
 * the gap is needed, not that the row should be hidden.
 *
 * `hour_of_day`/`daypart` (flank#4837) mismatch for a different reason:
 * ribeye-api#2526 made the grouped query correctly exclude delivery with no
 * hour of day at all (Meta, TikTok, some Google Ads), so the Total — still
 * the ungrouped, hour-agnostic aggregate — legitimately exceeds the rows.
 */
const TOTAL_ROW_MISMATCH_DIMS = new Set(['app_name', 'domain', 'publisher', 'hour_of_day', 'daypart']);

/** Which of the two explanations above applies to a mismatched dim. */
const HOUR_MISMATCH_DIMS = new Set(['hour_of_day', 'daypart']);

export const TOTAL_ROW_MISMATCH_NOTE =
  'This breakdown reads canonical APP/DOMAIN, which only carries impressions matched to an app or domain — a subset of all delivery. The Total still matches the KPI cards (all delivery), so it can be higher than the sum of the rows below.';

export const TOTAL_ROW_MISMATCH_HOUR_NOTE =
  "Meta and TikTok don't report an hour of delivery at all, and some Google Ads delivery doesn't either, so that delivery can't appear broken out by hour or daypart here, but it still counts in the Total above.";

export const isTotalRowMismatched = (dims: string[]): boolean => dims.some(dim => TOTAL_ROW_MISMATCH_DIMS.has(dim));

/** Picks the explanation matching which dim triggered `isTotalRowMismatched`. */
export const totalRowMismatchNote = (dims: string[]): string =>
  dims.some(dim => HOUR_MISMATCH_DIMS.has(dim)) ? TOTAL_ROW_MISMATCH_HOUR_NOTE : TOTAL_ROW_MISMATCH_NOTE;

/**
 * flank#4880 — GEOGRAPHY is the only canonical fact table without a Device or
 * Publisher column (ribeye-api's queryBuilder.ts FILTER_COLUMNS), so the API
 * silently drops those two filters for every query a geo-dimensioned widget
 * fires and reports it back via `ignored_filters`. Shared between
 * GeographicPerformance (the map) and ImpressionsByType's Geo tab — both
 * route to GEOGRAPHY the same way, so both need the same disclosure (the
 * tab was missed when the map got this originally — review, Lucas).
 */
export const GEO_IGNORED_FILTER_LABELS: Record<string, string> = {
  device_ids: 'Device',
  publishers: 'Publisher',
};

/**
 * Builds the disclosure sentence from the API's `ignored_filters`, or null
 * when nothing relevant was dropped. Exported standalone (no widget state
 * needed) so the text itself is unit-testable without rendering a card.
 */
export const geoIgnoredFilterNote = (ignoredFilters: string[]): string | null => {
  const names = ignoredFilters.reduce<string[]>((acc, key) => {
    const label = GEO_IGNORED_FILTER_LABELS[key];
    if (label) acc.push(label);
    return acc;
  }, []);
  if (names.length === 0) return null;
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names.at(-1)}` : names[0];
  const plural = names.length > 1;
  return `${list} ${plural ? "filters don't" : "filter doesn't"} apply to this breakdown — geography data doesn't carry ${plural ? 'those fields' : 'that field'}, so ${plural ? "they weren't" : "it wasn't"} applied to these numbers.`;
};
